 /* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Shared utility functions for GoodFud platform
 */

import { NutritionInfo, MacroTargets } from '../types';

/**
 * Calculate total nutrition from an array of nutrition info
 */
export function sumNutrition(nutritionArray: NutritionInfo[]): NutritionInfo {
  return nutritionArray.reduce(
    (total, current) => ({
      calories: total.calories + current.calories,
      protein: total.protein + current.protein,
      carbs: total.carbs + current.carbs,
      fat: total.fat + current.fat,
      fiber: (total.fiber || 0) + (current.fiber || 0),
      sugar: (total.sugar || 0) + (current.sugar || 0),
      sodium: (total.sodium || 0) + (current.sodium || 0),
      cholesterol: (total.cholesterol || 0) + (current.cholesterol || 0),
      saturated_fat: (total.saturated_fat || 0) + (current.saturated_fat || 0),
      trans_fat: (total.trans_fat || 0) + (current.trans_fat || 0),
    }),
    {
      calories: 0,
      protein: 0,
      carbs: 0,
      fat: 0,
      fiber: 0,
      sugar: 0,
      sodium: 0,
      cholesterol: 0,
      saturated_fat: 0,
      trans_fat: 0,
    }
  );
}

/**
 * Calculate macro percentages from nutrition info
 */
export function calculateMacroPercentages(nutrition: NutritionInfo): {
  protein: number;
  carbs: number;
  fat: number;
} {
  const proteinCals = nutrition.protein * 4;
  const carbsCals = nutrition.carbs * 4;
  const fatCals = nutrition.fat * 9;
  const totalCals = proteinCals + carbsCals + fatCals;

  if (totalCals === 0) {
    return { protein: 0, carbs: 0, fat: 0 };
  }

  return {
    protein: Math.round((proteinCals / totalCals) * 100),
    carbs: Math.round((carbsCals / totalCals) * 100),
    fat: Math.round((fatCals / totalCals) * 100),
  };
}

/**
 * Calculate progress towards macro targets
 */
export function calculateMacroProgress(
  current: NutritionInfo,
  targets: MacroTargets
): {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
} {
  const totalCalories = targets.protein_calories + targets.carbs_calories + targets.fat_calories;
  return {
    calories: totalCalories > 0 ? (current.calories / totalCalories) * 100 : 0,
    protein: targets.protein_grams > 0 ? (current.protein / targets.protein_grams) * 100 : 0,
    carbs: targets.carbs_grams > 0 ? (current.carbs / targets.carbs_grams) * 100 : 0,
    fat: targets.fat_grams > 0 ? (current.fat / targets.fat_grams) * 100 : 0,
  };
}

/**
 * Format date to YYYY-MM-DD
 */
export function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format nutrition value with unit
 */
export function formatNutrition(value: number, unit: string): string {
  return `${Math.round(value)}${unit}`;
}

/**
 * Calculate BMI from height and weight
 */
export function calculateBMI(heightCm: number, weightKg: number): number {
  const heightM = heightCm / 100;
  return weightKg / (heightM * heightM);
}

/**
 * Get BMI category
 */
export function getBMICategory(bmi: number): string {
  if (bmi < 18.5) return 'Underweight';
  if (bmi < 25) return 'Normal weight';
  if (bmi < 30) return 'Overweight';
  return 'Obese';
}

const EMAIL_MAX_LENGTH = 254;
const EMAIL_LOCAL_MAX_LENGTH = 64;

// Local part: letters, digits and the usual specials, no leading/trailing/double dots.
const EMAIL_LOCAL_RE = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*$/;

// Domain: dot-separated labels (no leading/trailing hyphen) ending in an alphabetic TLD of 2+ letters or a punycode (xn--) TLD.
const EMAIL_DOMAIN_RE = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+(?:[a-z]{2,}|xn--[a-z0-9-]{1,59})$/;

/**
 * Trim and lowercase an email, matching what the backend stores
 */
export function normalizeEmail(email: string): string {
  return (email || '').trim().toLowerCase();
}

/**
 * Check a phone number against the same rules as the backend
 * (backend/app/core/validators.py). Returns a specific, user-facing message
 * explaining what is wrong, or '' when the number is valid.
 *
 * Accepted: +<country code><number> (E.164), 91XXXXXXXXXX, or a bare 10-digit
 * Indian mobile number starting with 6-9.
 */
export function getPhoneError(raw: string): string {
  const p = (raw || '').replace(/[\s\-().]/g, '');
  if (!p) return '';

  if (p.startsWith('+')) {
    return /^\+[1-9]\d{7,14}$/.test(p)
      ? ''
      : 'Enter a valid number after the country code, e.g. +918790367123';
  }

  if (!/^\d+$/.test(p)) return 'Phone number can only contain digits (and an optional + at the start)';
  if (/^91[6-9]\d{9}$/.test(p)) return '';
  if (p.length !== 10) return 'Phone number must be 10 digits, or start with a country code like +91';
  if (!/^[6-9]/.test(p)) return 'Indian mobile numbers must start with 6, 7, 8 or 9';
  return '';
}

export function isValidPhone(raw: string): boolean {
  return !!raw && getPhoneError(raw) === '';
}

/**
 * Turn an API error `detail` into a displayable string. FastAPI returns 422
 * detail as an array of { loc, msg } objects, which can't be rendered directly.
 */
export function formatApiError(detail: any, fallback: string): string {
  if (Array.isArray(detail)) {
    return detail
      .map((d: any) => `${d.loc?.slice(1).join('.')}: ${String(d.msg).replace(/^Value error, /, '')}`)
      .join('; ');
  }
  return detail || fallback;
}

/**
 * Validate email format (syntax only; does not check the domain can receive mail)
 */
export function isValidEmail(email: string): boolean {
  const normalized = normalizeEmail(email);
  if (!normalized || normalized.length > EMAIL_MAX_LENGTH) return false;

  const parts = normalized.split('@');
  if (parts.length !== 2) return false;

  const [local, domain] = parts;
  if (!local || local.length > EMAIL_LOCAL_MAX_LENGTH) return false;

  return EMAIL_LOCAL_RE.test(local) && EMAIL_DOMAIN_RE.test(domain);
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  waitMs: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), waitMs);
  };
}
